import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { AssetFallbackIcon, Input, LoaderCircle } from '@skytech/ui'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { useDebounce } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import { NoResults } from '@/components/NoResults'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { Asset } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

const stringMatcher = (item: Asset) => [item.id, item.assetDescription]

export const Assets: FC = () => {
  const translate = useTranslation()
  const params = useParams()

  const assetLocationId = Number(params.assetLocationId)

  if (isNaN(assetLocationId)) throw Error(translate('Asset location ID is not a number!'))

  const { data, isError } = useQuery(queries.adminAssetList({ allAssets: true }), () =>
    adminResources.getAssetListWithFilters({ allAssets: true })
  )

  const [querySearch, setSearchQuery] = useSearchParamsByKey('asset-search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  const itemsFiltered =
    data?.assets.filter(
      asset =>
        asset.costObjectId === assetLocationId && getQueryFilter(searchFilter, stringMatcher)(asset)
    ) || []

  if (isError) return <LoadingError />
  if (!data) return <LoaderCircle />

  return (
    <div className="cost-object-assets">
      <div className="table-actions">
        <div className="search">
          <Input
            id="employee-asset-search"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>
      </div>

      {itemsFiltered.length > 0 ? (
        <div className="asset-list">
          {itemsFiltered.map(item => (
            <AssetListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </div>
      ) : (
        <NoResults />
      )}
    </div>
  )
}

const AssetListItem: FC<Asset & { searchFilter: string }> = ({
  assetDescription,
  id,
  imageUrl,
  assetType,
}) => {
  const navigate = useNavigate()

  return (
    <button
      className="cost-object-details-asset"
      onClick={() => navigate(APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id)))}
    >
      <div className="asset-name">
        <h4>{assetDescription}</h4>
      </div>
      <ImageWithIconFallback
        src={imageUrl || ''}
        alt="asset"
        className="asset-image"
        fallbackIcon={<AssetFallbackIcon assetType={assetType} />}
      />
    </button>
  )
}
